import DetectRTC from "detectrtc";
import { DEVICE_ORIENTATION_TYPES, DEVICE_TYPES } from "../constants/Constants";
import { getExactBrowserName } from "../shared/utility";
import { SUPPORTED_IOS_DEVICE_FOR_CHROME, SUPPORTED_IOS_SAFARI } from "../constants/BottomMenuConstants";
import { HELP_ARTICLE } from "../constants/LDConstants";
import { LogFirebase } from "./firestore";

class NavigatorService {
    isMobileDevice() {
        return /iPhone|iPad|iPod|Android|BlackBerry|Windows Phone/i.test(navigator.userAgent);
    }

    isIpadOS() {
        return /Macintosh/.test(navigator.userAgent) && navigator?.maxTouchPoints > 2;
    }

    isAndroid() {
        return /Android/i.test(navigator.userAgent);
    }

    mobileIpad() {
        return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
            navigator.userAgent
        );
    }

    isIosDevice() {
        const regex = /(iPhone|iPad|iPod)/i;
        const match = regex.exec(navigator.userAgent);
        return match !== null || this.isIpadOS();
    }

    isMobileDeviceOrIpadOs() {
        return this.isMobileDevice() || this.isIpadOS();
    }

    isUIWebView() {
        const ua = window.navigator.userAgent;
        return /iPhone|iPod|iPad/.test(ua) && !window.MSStream && !ua.includes("Safari");
    }

    isSafari() {
        const browserVersion = DetectRTC.browser.version;
        const browser = DetectRTC.browser.name.toLowerCase();
        const OSName = DetectRTC.osName.toLowerCase();
        const userAgent = navigator.userAgent;
        const isSafari = browser === "safari";
        const isChrome = browser === "chrome";
        // iOS Edge uses EdgiOS identifier and WebKit engine
        const isIOSEdge = userAgent.includes("EdgiOS");

        const isSupportedSafariIOS = OSName === "ios" && isSafari && browserVersion >= SUPPORTED_IOS_SAFARI;
        const isSupportedSafariMac = OSName === "mac os x" && isSafari && browserVersion >= SUPPORTED_IOS_SAFARI;
        const isSupportedChromeIOS = OSName === "ios" && isChrome && parseFloat(DetectRTC.osVersion) >= SUPPORTED_IOS_DEVICE_FOR_CHROME;
        // iOS Edge uses WebKit and follows iOS version requirements (same as Chrome on iOS)
        const isSupportedEdgeIOS = OSName === "ios" && isIOSEdge && parseFloat(DetectRTC.osVersion) >= SUPPORTED_IOS_DEVICE_FOR_CHROME;

        HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK =
            isSupportedSafariIOS || isSupportedSafariMac || isSupportedEdgeIOS
                ? HELP_ARTICLE.SAFARI_MOBILE_TAB_ARTICLES_NUMBER
                : HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK;

        return isSupportedSafariIOS || isSupportedSafariMac || isSupportedChromeIOS || isSupportedEdgeIOS;
    }

    getDeviceMemory() {
        if (navigator?.deviceMemory !== undefined) {
            return navigator.deviceMemory + " GB";
        }
    }

    getBatteryDetails() {
        return new Promise((resolve, reject) => {
            if (navigator?.getBattery !== undefined) {
                navigator.getBattery().then(function (batteryObj) {
                    resolve(batteryObj);
                });
            }
        });
    }

    displayResolution() {
        const screenWidth = window.screen.width || "";
        const screenHeight = window.screen.height || "";

        return `${screenWidth} x ${screenHeight}`;
    }

    isIFrameAvailabe() {
        return window.location !== window.parent.location;
    }

    getDeviceType() {
        let device_type = "";

        if (this.isAndroid()) {
            device_type = DEVICE_TYPES.ANDROID_BROWSER;
        } else if (this.isIosDevice() && (navigator.mediaDevices || this.isMobileDevice())) {
            device_type = DEVICE_TYPES.IOS_BROWSER;
        } else {
            device_type = DEVICE_TYPES.DESKTOP;
        }

        return device_type;
    }

    getOrientation() {
        if ("screen" in window && "orientation" in window.screen) {
            if (this.isMobileDevice()) {
                switch (window.screen.orientation.angle) {
                    case 0:
                        return DEVICE_ORIENTATION_TYPES.ONE_PORTRAIT;
                    case 180:
                        return DEVICE_ORIENTATION_TYPES.TWO_UPSIDE_DOWN;
                    case 90:
                        return DEVICE_ORIENTATION_TYPES.THREE_LANDSCAPE;
                    case -90:
                        return DEVICE_ORIENTATION_TYPES.FOUR_LANDSCAPE_REVERSED;
                    default:
                        return DEVICE_ORIENTATION_TYPES.ZERO_UNKNOWN;
                }
            } else {
                return this.getDeviceType() === DEVICE_TYPES.DESKTOP
                    ? DEVICE_ORIENTATION_TYPES.THREE_LANDSCAPE
                    : DEVICE_ORIENTATION_TYPES.ZERO_UNKNOWN;
            }
        }

        if (window.matchMedia("(orientation: portrait)").matches) {
            return DEVICE_ORIENTATION_TYPES.ONE_PORTRAIT;
        }

        if (window.matchMedia("(orientation: landscape)").matches) {
            return window.innerWidth > window.innerHeight
                ? DEVICE_ORIENTATION_TYPES.THREE_LANDSCAPE
                : DEVICE_ORIENTATION_TYPES.FOUR_LANDSCAPE_REVERSED;
        }

        if (window.matchMedia("(orientation: portrait-secondary)").matches) {
            return DEVICE_ORIENTATION_TYPES.TWO_UPSIDE_DOWN;
        }

        return DEVICE_ORIENTATION_TYPES.ZERO_UNKNOWN;
    }
    isChromiumBrowser() {
        return !!navigator.userAgentData?.brands.find((data) => data.brand === "Chromium");
    }

    isLatestAndroidChrome() {
        const OSName = DetectRTC.osName.toLowerCase();
        let { browserName, browserVersion } = getExactBrowserName();
        browserName = browserName.toLowerCase();
        browserVersion = parseFloat(browserVersion);
        return OSName == "android" && browserName == "chrome" && browserVersion >= 85;
    }

    isImageCaptureSupported(videoTrack) {
        const isChromiumBrowser = this.isChromiumBrowser();
        return videoTrack?.[0]?.mediaStreamTrack && isChromiumBrowser && "ImageCapture" in window;
    }

    isChrome() {
        const unsupportedList = ["edga", "edge", "firefox", "opera"];
        let isSupportedBrowser = true;
        const OSName = DetectRTC.osName.toLowerCase();
        const browserName = DetectRTC.browser.name.toLowerCase();
        const userAgent = navigator.userAgent.toLowerCase();
        LogFirebase("Andorid", "isChrome", {
            OSName: OSName,
            browserName: browserName,
            userAgent: userAgent,
        });
        // Check for Android Chrome-based browsers and WebViews (e.g., Chrome, Samsung Browser, WhatsApp wa4a)
        // Use browserName, userAgent chrome check, and explicit wa4a check for maximum compatibility
        const isWhatsAppWebView = browserName === "wa4a" || userAgent.includes("wa4a");
        const isAndroidChromeEngine = OSName === "android" && (userAgent.includes("chrome") || browserName === "chrome" || isWhatsAppWebView);

        if (isAndroidChromeEngine) {
            for (const browser of unsupportedList) {
                if (userAgent.includes(browser)) {
                    // Check for special case: Motorola Edge device with Edge browser
                    if ((browser === "edge" || browser === "edga") && userAgent.includes("motorola")) {
                        continue; // Skip marking as unsupported
                    }
                    isSupportedBrowser = false;
                    break;
                }
            }
        } else {
            isSupportedBrowser = false; // Not Android or not Chrome browser
        }

        return isSupportedBrowser;
    }

    /**
     * Detects if the browser is an Android WebView (e.g., WhatsApp, Facebook in-app browser)
     * WebViews use Chrome engine but are not the actual Chrome browser
     * @returns {boolean} true if Android WebView, false otherwise
     */
    isAndroidWebView() {
        const OSName = DetectRTC.osName.toLowerCase();
        const browserName = DetectRTC.browser.name.toLowerCase();
        const userAgent = navigator.userAgent.toLowerCase();

        // Must be Android
        if (OSName !== "android") {
            return false;
        }

        // Check if it's a WebView (not Chrome browser but uses Chrome engine)
        const isChromeBrowser = browserName === "chrome";
        const usesChromeEngine = userAgent.includes("chrome");
        const isWhatsAppWebView = browserName === "wa4a" || userAgent.includes("wa4a");
        const isSamsungBrowser = browserName === "samsung" || userAgent.includes("samsungbrowser");

        // It's a WebView if: uses Chrome engine but browser is not Chrome/Samsung
        // WhatsApp (wa4a) is explicitly a WebView
        if (isWhatsAppWebView) {
            return true;
        }

        // If browser is Chrome or Samsung, it's not a WebView
        if (isChromeBrowser || isSamsungBrowser) {
            return false;
        }

        // If uses Chrome engine but browser name is something else, it's likely a WebView
        if (usesChromeEngine && !isChromeBrowser) {
            return true;
        }

        return false;
    }

    isMac() {
        return navigator.userAgent.includes("Macintosh") || navigator.userAgent.includes("Mac OS X");
    }

    isSafariBrowser() {
        const os = DetectRTC.osName.toLowerCase();
        const browserName = DetectRTC.browser.name.toLowerCase();
        const isSafariOS = os === "ios" || os === "mac os x";
        const isSafariBrowser = browserName === "safari";

        return isSafariOS && isSafariBrowser;
    }
}
export default new NavigatorService();
